/**
 * @author Fernando Ruano
 * @file index.jsx
 * @description Header Component.
 */

// Dependencies
import React from 'react';
import { Layout, Avatar } from 'antd';
import PropTypes from 'prop-types';
import { MenuOutlined } from '@ant-design/icons';

// Components
// import LanguageSelector from '@nephro/components/LanguageSelector';
import PageHeader from 'components/PageHeader';
import AppointmentNotification from 'pages/AppointmentNotification';
import { useLocation } from 'react-router';

// Helpers
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';
import { capitalizeFirstLetter } from '@nephro/commons/helpers/string-helper';
import { transformPatientName } from 'commons/helpers/utils';

// Assets
import baxterLogo from '@nephro/assets/logoNefroPlus.png';

// Styles
import styles from './styles.module.css';

// Dependencies Props
const { Header } = Layout;

/**
 * AppRoutes Function component.
 * @param { Object } props - React PropTypes
 * @property { Boolean } broken - Indicate when is mobile or ipad screen.
 * @property { Function } onShowDrawer - Function to show drawer menu.
 * @property { Boolean } visible - Indicate when sider menu is visible.
 * @return { Node } React node containing app header.
 */

const AppHeader = ({ broken, onShowDrawer, visible }) => {
  const [avatarChar, setAvatarChar] = React.useState(null);
  const [userName, setUserName] = React.useState(null);
  const location = useLocation();

  React.useEffect(() => {
    const getUserData = async () => {
      const userInfo = await getStorageItem('user');
      const isPatient = await getStorageItem('isPatient');
      const firstName = capitalizeFirstLetter(
        isPatient ? userInfo.first_name : userInfo?.user?.first_name
      );
      const lastName = capitalizeFirstLetter(
        isPatient ? userInfo.last_name : userInfo?.user?.last_name
      );
      const firstNameLetter = firstName[0];
      const lastNameLetter = lastName[0];
      setAvatarChar(`${firstNameLetter}${lastNameLetter}`);
      setUserName(transformPatientName(`${firstName} ${lastName}`));
    };
    getUserData();
  }, [location]);

  if (
    (location.pathname === '/main/dashboard' ||
      location.pathname === '/main/scheduled-appointments') &&
    broken
  ) {
    return (
      <>
        <Header className={styles.headerStyle}>
          <div className="flex flex-wrap items-center justify-between ml-1 lg:ml-5 w-full">
            <img src={baxterLogo} className="w-40 md:w-44" />
            <div>
              <AppointmentNotification />
              <Avatar
                gap={30}
                className={styles.avatarStyle}
                shape="square"
                size="large">
                {avatarChar}
              </Avatar>
              <MenuOutlined
                className={styles.triggerMobile}
                onClick={() => onShowDrawer(!visible)}
              />
            </div>
          </div>
        </Header>
        <PageHeader />
      </>
    );
  }
  return (
    <Header className={styles.headerStyle}>
      {broken ? (
        <>
          <div>{/* <PageHeader /> */}</div>
          <div className="flex flex-row items-center ml-5">
            <MenuOutlined
              className={`${styles.triggerMobile}`}
              onClick={() => onShowDrawer(!visible)}
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-row items-center ml-2">
            <img src={baxterLogo} className="w-40 md:w-44" />
          </div>
          <div className="flex flex-row items-center">
            {location.pathname === '/main/dashboard' && (
              <AppointmentNotification />
            )}
            <div className="flex flex-row">
              <h2 className="mr-7 mb-0 text-base text-white">{userName}</h2>
            </div>
            <Avatar
              gap={30}
              className={styles.avatarStyle}
              shape="square"
              size="large">
              {avatarChar}
            </Avatar>
            {/* <LanguageSelector /> */}
          </div>
        </>
      )}
    </Header>
  );
};

/**
 * @name AppHeader.propTypes
 * @type { propTypes } React PropTypes.
 * @property { Boolean } broken - Is sider broken boolean.
 * @property { Function } onShowDrawer - On Show Drawer function.
 * @property { Boolean } visible - Is sider visible boolean.
 */
AppHeader.propTypes = {
  broken: PropTypes.bool,
  onShowDrawer: PropTypes.func,
  visible: PropTypes.bool
};

/**
 * Initialize default properties.
 */
AppHeader.defaultProps = {
  broken: false,
  visible: false
};

export default AppHeader;
